import { focusManager, useQueryClient } from '@tanstack/react-query';

import { resolvePath } from '@/lib/formatters/resolveEnvUrl';

import { useLogout } from './useLogout';

const LOGOUT_STORAGE_KEYS = ['red1000Jwt', 'partnerId'] as const;

/**
 * Centraliza todo o fluxo de logout para evitar race conditions e duplicação
 * de lógica entre os pontos que oferecem a ação "Sair" (ProfileMenu,
 * LocalNavigation, etc.).
 *
 * O redirect acontece SOMENTE depois que o `logout()` resolve com sucesso.
 * Isso garante que a request ao backend termine de fato antes de sair da app —
 * se o redirect ficasse num `finally`, uma rejeição rápida (erro de rede/CORS/
 * status != 2xx) dispararia o `window.location.href` na hora e a navegação
 *
 * Em caso de falha, restauramos o estado (pending/focus) e propagamos o erro,
 * sem redirecionar — assim o problema fica visível em vez de mascarado.
 */
export function usePerformLogout(): () => Promise<void> {
  const queryClient = useQueryClient();
  const { mutateAsync: logout } = useLogout();

  return async (): Promise<void> => {
    focusManager.setFocused(false);

    try {
      await queryClient.cancelQueries();
      await logout();
    } catch (error) {
      focusManager.setFocused(true);
      throw error;
    }

    LOGOUT_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    window.location.href = resolvePath('home');
  };
}
